import { z } from 'zod';

import {
  isoDateTimeSchema,
  nonNegativeIntSchema,
  semverSchema,
  uuidSchema,
} from '../common/primitives';
import { nodeCapacitySchema } from '../fleet/capacity';
import { nodeInventorySchema } from '../fleet/inventory';
import { nodeMetricsSchema } from '../fleet/metrics';
import { preflightReportSchema } from '../fleet/preflight';

/**
 * Verträge für Aufrufe in Richtung Master -> Worker.
 *
 * In M1 ist das nur die Statusabfrage. Sie ist trotzdem wichtig: Sie ist der
 * Beweis, dass die Gegenrichtung authentifiziert funktioniert, bevor in M2
 * Provisionierungsbefehle darüber laufen. Ein Kanal, der erst dann zum ersten
 * Mal benutzt wird, wenn etwas Wichtiges darüber soll, ist ein Kanal, dessen
 * Fehler man zum ungünstigsten Zeitpunkt findet.
 */

/** Antwort des Agents auf `GET /v1/status`. */
export const agentStatusResponseSchema = z.object({
  /** Node-ID aus Sicht des Agents — muss mit der Erwartung des Masters übereinstimmen. */
  nodeId: uuidSchema.nullable(),
  agentVersion: semverSchema,
  /** Laufzeit des Agent-Prozesses in Sekunden. */
  uptimeSeconds: nonNegativeIntSchema,
  /** `false`, solange der Agent noch nicht registriert ist. */
  enrolled: z.boolean(),
  generation: z.number().int().positive().nullable(),
  capacity: nodeCapacitySchema,
  metrics: nodeMetricsSchema,
  preflight: preflightReportSchema,
  /** `null`, wenn der Agent das Inventar nicht erheben konnte — nie ersatzweise `[]`. */
  inventory: nodeInventorySchema.nullable(),
  respondedAt: isoDateTimeSchema,
});

export type AgentStatusResponse = z.infer<typeof agentStatusResponseSchema>;

/** Antwort des Agents auf `GET /v1/ping` — bewusst minimal für schnelle Erreichbarkeitsprüfungen. */
export const agentPingResponseSchema = z.object({
  pong: z.literal(true),
  nodeId: uuidSchema.nullable(),
  agentVersion: semverSchema,
  respondedAt: isoDateTimeSchema,
});

export type AgentPingResponse = z.infer<typeof agentPingResponseSchema>;
